import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, Globe, Share2, Settings, Plus, MapPin, Clock, Code2 } from "lucide-react";
import { useVisitorRules } from "@/hooks/useGrowthMarketing";
import { Switch } from "@/components/ui/switch";

type Tab = "overview" | "website" | "social" | "rules" | "setup";

interface Visitor {
  id: string;
  city: string;
  page: string;
  source: string;
  ts: number;
}

const SOURCES = ["Google Organic", "Facebook", "Direct", "LinkedIn", "Bing"];
const PAGES = ["/services", "/contact", "/dementia-care", "/pricing", "/about"];
const CITIES = ["Alexandria, VA", "Arlington, VA", "Fairfax, VA", "Reston, VA", "Springfield, VA"];

function makeVisitor(): Visitor {
  return {
    id: Math.random().toString(36).slice(2),
    city: CITIES[Math.floor(Math.random() * CITIES.length)],
    page: PAGES[Math.floor(Math.random() * PAGES.length)],
    source: SOURCES[Math.floor(Math.random() * SOURCES.length)],
    ts: Date.now(),
  };
}

export default function VisitorTracking() {
  const [tab, setTab] = useState<Tab>("overview");
  const [visitors, setVisitors] = useState<Visitor[]>(() =>
    Array.from({ length: 5 }, (_, i) => ({ ...makeVisitor(), ts: Date.now() - i * 60000 })),
  );
  const intervalRef = useRef<number | null>(null);

  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleStep, setRuleStep] = useState(1);
  const [ruleName, setRuleName] = useState("");
  const [ruleTrigger, setRuleTrigger] = useState("page_visit");
  const [ruleAction, setRuleAction] = useState("notify");
  const { items: rules, add: addRule, toggle: toggleRule, loading: rulesLoading } = useVisitorRules();

  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      setVisitors((vs) => [makeVisitor(), ...vs].slice(0, 30));
    }, 45000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const saveRule = async () => {
    try {
      await addRule({ name: ruleName || "Untitled rule", trigger_type: ruleTrigger, action_type: ruleAction });
      setRuleOpen(false);
      setRuleStep(1);
      setRuleName("");
      toast.success("Rule saved");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save rule");
    }
  };

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "overview", label: "Overview", icon: Eye },
    { key: "website", label: "Website", icon: Globe },
    { key: "social", label: "Social", icon: Share2 },
    { key: "rules", label: "Rules", icon: Settings },
    { key: "setup", label: "Setup", icon: Code2 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Visitors today", value: "247", delta: "+18%" },
            { label: "Avg session", value: "2:14", delta: "+0:22" },
            { label: "Conversion rate", value: "4.2%", delta: "+0.8pp" },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground">{s.label}</div>
                <div className="text-3xl font-bold text-navy mt-1">{s.value}</div>
                <div className="text-xs text-success mt-1">{s.delta} vs last week</div>
              </CardContent>
            </Card>
          ))}
          <Card className="md:col-span-3">
            <CardHeader><CardTitle className="text-base">Live Visitor Feed</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-1">
                {visitors.map((v) => (
                  <div key={v.id} className="flex items-center justify-between py-2 border-b last:border-0 animate-in slide-in-from-top-2 fade-in duration-500">
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
                      <span className="text-sm font-medium">{v.page}</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {v.city}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs">{v.source}</Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {Math.max(1, Math.round((Date.now() - v.ts) / 60000))}m ago</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "website" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Top Pages</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { page: "/services", views: 1240, time: "3:21" },
                { page: "/dementia-care", views: 870, time: "4:08" },
                { page: "/contact", views: 612, time: "1:14" },
                { page: "/pricing", views: 488, time: "2:46" },
              ].map((p) => (
                <div key={p.page} className="flex items-center justify-between py-3 border-b last:border-0">
                  <span className="font-medium text-sm">{p.page}</span>
                  <div className="flex gap-6 text-sm text-muted-foreground">
                    <span>{p.views.toLocaleString()} views</span>
                    <span>Avg {p.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "social" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: "Facebook", followers: 1240, engagement: "3.4%", color: "bg-blue-500" },
            { name: "LinkedIn", followers: 580, engagement: "5.1%", color: "bg-blue-700" },
            { name: "Instagram", followers: 920, engagement: "2.8%", color: "bg-pink-500" },
            { name: "Google Business", followers: 0, engagement: "—", color: "bg-gold" },
          ].map((s) => (
            <Card key={s.name}>
              <CardContent className="pt-6 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg ${s.color} flex items-center justify-center text-white font-bold`}>{s.name[0]}</div>
                <div className="flex-1">
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-sm text-muted-foreground">{s.followers} followers · {s.engagement} engagement</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === "rules" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Automation Rules ({rules.length})</CardTitle>
              <Button onClick={() => setRuleOpen(true)} className="bg-gold text-gold-foreground hover:bg-gold/90"><Plus className="h-4 w-4" /> New rule</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rulesLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
              {!rulesLoading && rules.length === 0 && <div className="text-sm text-muted-foreground">No rules yet. Click "New rule" to create one.</div>}
              {rules.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium text-sm">{r.name}</div>
                    <div className="text-xs text-muted-foreground">When {r.trigger_type.replace("_", " ")} → {(r.action as any)?.type ?? "notify"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={r.enabled} onCheckedChange={(v) => toggleRule(r.id, v)} />
                    <Badge className={r.enabled ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}>{r.enabled ? "Active" : "Paused"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "setup" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Tracking snippet</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">Paste this script in the &lt;head&gt; of your website.</p>
            <pre className="bg-navy text-cream p-4 rounded-lg text-xs overflow-x-auto"><code>{`<script src="https://cdn.legacygrowth.com/track.js" data-site="agency-id"></script>`}</code></pre>
            <Button variant="outline" className="mt-3" onClick={() => { navigator.clipboard.writeText('<script src="https://cdn.legacygrowth.com/track.js" data-site="agency-id"></script>'); toast.success("Copied"); }}>Copy snippet</Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={ruleOpen} onOpenChange={setRuleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New rule — Step {ruleStep} of 3</DialogTitle>
          </DialogHeader>
          {ruleStep === 1 && (
            <div className="space-y-3">
              <Label>Rule name</Label>
              <Input value={ruleName} onChange={(e) => setRuleName(e.target.value)} placeholder="e.g. Hot pricing-page visitor" />
            </div>
          )}
          {ruleStep === 2 && (
            <div className="space-y-3">
              <Label>Trigger when…</Label>
              <Select value={ruleTrigger} onValueChange={setRuleTrigger}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="page_visit">Specific page visited</SelectItem>
                  <SelectItem value="time_on_site">Time on site &gt; 2min</SelectItem>
                  <SelectItem value="returning">Returning visitor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {ruleStep === 3 && (
            <div className="space-y-3">
              <Label>Then…</Label>
              <Select value={ruleAction} onValueChange={setRuleAction}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="notify">Notify me</SelectItem>
                  <SelectItem value="popup">Show popup CTA</SelectItem>
                  <SelectItem value="email">Send follow-up email</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            {ruleStep > 1 && <Button variant="outline" onClick={() => setRuleStep(ruleStep - 1)}>Back</Button>}
            {ruleStep < 3 ? (
              <Button onClick={() => setRuleStep(ruleStep + 1)} className="bg-gold text-gold-foreground hover:bg-gold/90">Next</Button>
            ) : (
              <Button onClick={saveRule} className="bg-gold text-gold-foreground hover:bg-gold/90">Save rule</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
